import { css } from '@emotion/react';
import React from 'react';

import { Box } from '@/box';
import type { TransportOption as TransportOptionConfig } from '@/hotel/models/hotel.model';
import AnchorLink from '@/hox-core/components/controls/AnchorLink.component';
import { useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodyCopy, Heading } from '@/ui/typography';

import TransportOption from './TransportOption.component';

interface TransportOptionsProps {
  transportOptions: TransportOptionConfig[];
  parkingTitle: string | undefined;
  parkingText: string | undefined;
}

const TransportOptions: React.FC<TransportOptionsProps> = (props) => {
  return (
    <>
      <div
        css={css`
          display: grid;
          column-gap: 16px;
          grid-template-columns: 1fr 1fr;

          @media all and (max-width: 500px) {
            grid-template-columns: 1fr;
          }
        `}
      >
        {props.transportOptions.map((option) => (
          <TransportOption {...option} key={option.mode} />
        ))}
      </div>
      {props.parkingTitle && (
        <div>
          <Heading as="h6" style="heading6">
            {props.parkingTitle}
          </Heading>
          <Spacer s="xs" />
          <BodyCopy>
            {props.parkingText && <Markdown source={props.parkingText} />}
          </BodyCopy>
        </div>
      )}
    </>
  );
};

interface WhereToFindUsProps {
  mapImageUrl?: string;
  googleMapsUrl?: string;
  transportOptions: TransportOptionConfig[];
  parkingTitle: string | undefined;
  parkingText: string | undefined;
}

export const WhereToFindUs = (props: WhereToFindUsProps) => {
  const {
    colors,
    componentProperties: { confirmation },
  } = useTheme();
  const { t } = useTranslation('findUs');
  return (
    <div>
      <Heading
        as="h2"
        style="heading5"
        css={css({
          ...confirmation?.whereToFindUsCard?.title,
          ['@media all and (max-width: 730px)']:
            confirmation?.whereToFindUsCard?.title?.mobile,
        })}
      >
        {t('whereToFindUs')}
      </Heading>
      <Spacer s="m" />
      <div
        css={css`
          display: flex;
          color: ${colors.textPrimary};

          .hotel-map {
            cursor: pointer;
          }

          @media all and (max-width: 640px) {
            display: block;
          }
        `}
      >
        <Box
          width={[1, 1 / 2]}
          marginInlineEnd={[0, '16px']}
          marginBottom={['40px', 0]}
        >
          <img
            src={props.mapImageUrl}
            alt="Map to the hotel"
            onClick={
              props.googleMapsUrl
                ? () => window.open(props.googleMapsUrl, '_blank')
                : undefined
            }
            role="button"
            css={css`
              width: 100%;
              display: block;
            `}
          />
        </Box>
        <Box width={[1, 1 / 2]} marginInlineStart={[0, '16px']}>
          <TransportOptions
            transportOptions={props.transportOptions}
            parkingText={props.parkingText}
            parkingTitle={props.parkingTitle}
          />
        </Box>
      </div>
      {/* @TODO: This should just be a text field - no need for direct styles */}
      {props.googleMapsUrl && (
        <>
          <Spacer s="m" />
          <BodyCopy>
            <AnchorLink
              href={props.googleMapsUrl}
              openInNewTab
              color={
                confirmation?.whereToFindUsCard?.planYourJourneyLink?.color ??
                '#236A94'
              }
            >
              {t('planYourJourney')}
            </AnchorLink>
          </BodyCopy>
        </>
      )}
    </div>
  );
};
